import { AllIntegrations } from '@/hooks/useIntegration';
import type { DropdownOption } from '@/types/dropdown.types';
import { normalizeOriginName } from '@/utils/statisticValidation.util';

const INTEGRATION_KEY_SET = new Set(
  Object.keys(AllIntegrations).map(k => k.toLowerCase())
);

type GroupOption = DropdownOption & {
  isGroup?: boolean;
  items?: DropdownOption[];
};

/**
 * Formats a raw source value for display using the same logic as the main tables.
 * Converts backend values like "self", "account" to user-friendly names like "Chariot".
 */
export const formatSourceForDisplay = (
  rawSource: string | undefined | null,
  sourceFilterOptions: GroupOption[] = []
): string => {
  if (!rawSource) {
    return 'Praetorian';
  }

  // Find Praetorian group and its values (normalize to lowercase)
  const chariotGroup = sourceFilterOptions.find(
    sf => sf.isGroup && sf.label === 'Praetorian'
  );
  const chariotSourceValues =
    chariotGroup?.items?.map(item => String(item.value).toLowerCase()) || [];

  // If the source is a Praetorian capability, always show 'Praetorian' (case-insensitive)
  if (chariotSourceValues.includes(String(rawSource).toLowerCase())) {
    return 'Praetorian';
  }

  // Existing logic for integrations and manual
  const lowerSource = String(rawSource).toLowerCase();
  const isIntegration = INTEGRATION_KEY_SET.has(lowerSource);
  const isManual = rawSource.endsWith('@praetorian.com');

  const getFormattedLabel = (sourceValue: string): string => {
    let foundLabel: string | undefined;

    const chariotGroupFromFilters = sourceFilterOptions.find(
      sf => sf.isGroup && sf.label === 'Praetorian'
    );
    if (chariotGroupFromFilters?.items) {
      foundLabel = chariotGroupFromFilters.items.find(
        item => item.value === sourceValue
      )?.label;
    }

    if (!foundLabel) {
      const integrationOption = sourceFilterOptions.find(
        option => !option.isGroup && option.value === sourceValue
      );
      foundLabel = integrationOption?.label;
    }

    return foundLabel || normalizeOriginName(sourceValue);
  };

  if (isManual) {
    return 'Import';
  }

  if (isIntegration) {
    return getFormattedLabel(rawSource);
  }

  // Handle common Praetorian source values that should display as "Praetorian"
  const chariotSources = [
    'self',
    'account',
    'seed',
    'provided',
    'adhoc',
    '@praetorian.com',
  ];

  if (
    chariotSources.some(source =>
      String(rawSource).toLowerCase().includes(source.toLowerCase())
    )
  ) {
    return 'Praetorian';
  }

  // Robust fallback: any non-integration, non-manual source is 'Praetorian'
  return 'Praetorian';
};

/**
 * Returns true when the source value identifies a Praetorian-origin risk
 * (internal capabilities, manual imports, or any value that is not a known
 * third-party integration).
 *
 * Checks both the legacy `source` field and the newer canonical `origins` array.
 * If any entry in `origins` matches a known integration key, the risk is
 * considered third-party origin (returns false).
 */
export function isPraetorianOrigin(
  rawSource: string | undefined | null,
  origins?: string[]
): boolean {
  if (origins?.some(o => INTEGRATION_KEY_SET.has(o.toLowerCase()))) {
    return false;
  }
  if (!rawSource) return true;
  if (rawSource.endsWith('@praetorian.com')) return true;
  return !INTEGRATION_KEY_SET.has(rawSource.toLowerCase());
}

// Utility to aggregate all unique sources for an asset or list of assets
// Returns a sorted array of unique sources (integrationSource, allSources, source)

/**
 * Asset-like object with source properties.
 * This is a simplified type representing the source-related properties
 * that can exist on Asset objects.
 */
interface AssetWithSources {
  allSources?: string[];
  integrationSource?: string;
  source?: string;
  [key: string]: unknown; // Allow other properties
}

export function aggregateAllSourcesForAsset(
  assetOrAssets: AssetWithSources | AssetWithSources[]
): string[] {
  const assets = Array.isArray(assetOrAssets) ? assetOrAssets : [assetOrAssets];
  const sourceSet = new Set<string>();

  assets.forEach(asset => {
    // Add allSources if present (array)
    if (Array.isArray(asset.allSources)) {
      asset.allSources.forEach(src => {
        if (src && typeof src === 'string') sourceSet.add(src);
      });
    }
    // Add integrationSource if present
    if (
      asset.integrationSource &&
      typeof asset.integrationSource === 'string'
    ) {
      sourceSet.add(asset.integrationSource);
    }
    // Add source if present
    if (asset.source && typeof asset.source === 'string') {
      sourceSet.add(asset.source);
    }
  });

  // Remove empty or dash values
  const filtered = Array.from(sourceSet).filter(src => src && src !== '-');
  // Sort alphabetically
  return filtered.sort((a, b) => a.localeCompare(b));
}
